/**
 * ProjectFeatureRouter - Unified Architecture Router
 * Routes to appropriate feature components based on the feature parameter
 * Follows the /projects/:projectId/:feature pattern from unified architecture
 */

import React from 'react';
import { useParams, Navigate } from 'react-router-dom';

// Feature component imports
import { ScenarioManagement } from '../../pages/ScenarioManagement';
import { Locations } from '../../pages/Locations';
import { Vehicles } from '../../pages/Vehicles';
import { Jobs } from '../../pages/Jobs';
import { Optimization } from '../../pages/Optimization';
import { OptimizationResults } from '../../pages/OptimizationResults';
import { MapView } from '../../pages/MapView';
import { ScheduleView } from '../../pages/ScheduleView';
import { FleetManagement } from '../../pages/FleetManagement';
import { CostModeling } from '../../pages/CostModeling';
import { DataManagement } from '../../pages/DataManagement';
import { ProductsPage } from '../../pages/ProductsPage';
import { ShipmentsPage } from '../../pages/ShipmentsPage';
import SkillsPage from '../../pages/SkillsPage';
import { EnhancedVehiclesV2 } from '../../pages/EnhancedVehiclesV2';
import { Dashboard } from '../../pages/Dashboard';

// VRPFeature type definition moved locally
type VRPFeature = 
  | 'dashboard' | 'vehicles' | 'enhanced-vehicles' | 'locations'
  | 'jobs' | 'products' | 'shipments' | 'skills' | 'optimization'
  | 'optimization-results' | 'map' | 'schedule' | 'scenarios'
  | 'data' | 'data-management';

export const ProjectFeatureRouter: React.FC = () => {
  const params = useParams<{ projectId: string; feature: string }>();
  const { projectId, feature } = params;
  
  // Debug logging to verify params are received
  console.log('🔍 [ProjectFeatureRouter] Route params:', {
    params,
    projectId,
    feature,
    pathname: window.location.pathname
  });
  
  // Basic validation - more sophisticated validation will be handled by Toolpad
  if (!projectId) {
    console.warn(`[ProjectFeatureRouter] No project ID provided, redirecting to projects`);
    return <Navigate to="/projects" replace />;
  }

  // Validate feature parameter
  if (!feature) {
    console.warn(`[ProjectFeatureRouter] No feature specified, redirecting to dashboard`);
    return <Navigate to={`/projects/${projectId}/dashboard`} replace />;
  }

  // Route to appropriate feature component
  switch (feature as VRPFeature) {
    case 'dashboard':
      return <Dashboard />;
      
    case 'scenarios':
      return <ScenarioManagement />;
      
    case 'locations':
      return <Locations />;
      
    case 'vehicles':
      return <Vehicles />;
      
    case 'enhanced-vehicles':
      return <EnhancedVehiclesV2 />;
      
    case 'jobs':
      return <Jobs />;
      
    case 'products':
      return <ProductsPage />;
      
    case 'shipments':
      return <ShipmentsPage />;
      
    case 'skills':
      return <SkillsPage />;
      
    case 'optimization':
      return <Optimization />;
      
    case 'optimization-results':
      return <OptimizationResults />;
      
    case 'map':
      return <MapView />;
      
    case 'schedule':
      return <ScheduleView />;
      
    case 'fleet':
      return <FleetManagement />;
      
    case 'costs':
      return <CostModeling />;
      
    case 'data':
    case 'data-management':
      return <DataManagement />;
      
    default:
      console.warn(`[ProjectFeatureRouter] Unknown feature: ${feature}, redirecting to dashboard`);
      return <Navigate to={`/projects/${projectId}/dashboard`} replace />;
  }
};